import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import CalculatorShell from "@/components/CalculatorShell";
import Field from "@/components/Field";
import ResultStat from "@/components/ResultStat";
import { toNum } from "@/lib/finance";
import { fmtUSD } from "@/lib/format";
import { STATE_TAX_LIST } from "@/data/stateTax";

export default function SalaryPage() {
    const [annual, setAnnual] = useState(85000);
    const [hoursPerWeek, setHoursPerWeek] = useState(40);
    const [weeksPerYear, setWeeksPerYear] = useState(50);

    const rows = useMemo(() => {
        const a = toNum(annual);
        const h = toNum(hoursPerWeek);
        const w = toNum(weeksPerYear);
        const hourly = h * w > 0 ? a / (h * w) : 0;
        return {
            hourly,
            daily: hourly * (h / 5),
            weekly: a / (w || 52),
            biweekly: (a / (w || 52)) * 2,
            monthly: a / 12,
            annual: a,
        };
    }, [annual, hoursPerWeek, weeksPerYear]);

    return (
        <CalculatorShell
            slug="salary"
            explainer={
                              <>
                    <h2>How this salary calculator works</h2>
                    <p>
                        A salary calculator converts between hourly wages and annual salary, and breaks down
                        gross pay across every pay period. Use it to compare job offers expressed in different
                        formats, or to understand exactly what your hourly rate translates to annually.
                    </p>
                    <h3>How it works</h3>
                    <ul>
                        <li><strong>Hourly to annual:</strong> rate × hours per week × 52 weeks — the standard full-time baseline.</li>
                        <li><strong>Annual to hourly:</strong> salary ÷ (hours per week × 52) — useful for comparing salaried positions to hourly ones.</li>
                        <li><strong>Gross vs. net:</strong> the calculator shows gross pay before taxes and deductions; take-home will be lower.</li>
                        <li><strong>Pay period conversions:</strong> annual ÷ 12 for monthly, ÷ 26 for biweekly, ÷ 52 for weekly.</li>
                        <li><strong>Overtime threshold:</strong> federal FLSA requires 1.5× your regular rate for hours over 40 per week.</li>
                    </ul>
                    <h3>Tips</h3>
                    <ul>
                        <li>Calculate the effective hourly rate of any salaried role before comparing it to an hourly offer — long hours can make a salary worse than it looks.</li>
                        <li>Factor in benefits — health insurance, 401(k) match, PTO — when comparing total compensation across offers.</li>
                        <li>If you're self-employed, add 7.65% to your rate to cover the employer portion of FICA taxes you now pay yourself.</li>
                    </ul>
                </>
            }
        >
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                <div className="lg:col-span-3 bg-card rounded-xl border border-border p-6 sm:p-8 space-y-5">
                    <Field label="Annual salary" prefix="$" value={annual} onChange={setAnnual} dataTestId="calc-input-annual" />
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Hours per week" value={hoursPerWeek} onChange={setHoursPerWeek} dataTestId="calc-input-hours" />
                        <Field label="Weeks worked" value={weeksPerYear} onChange={setWeeksPerYear} dataTestId="calc-input-weeks" hint="Subtract vacation" />
                    </div>
                </div>
                <div className="lg:col-span-2">
                    <ResultStat
                        label="Hourly wage"
                        value={rows.hourly}
                        format={(n) => fmtUSD(n, { decimals: 2 })}
                        secondary={[
                            { label: "Monthly", value: rows.monthly, format: (n) => fmtUSD(n, { decimals: 0 }), testId: "calc-result-monthly" },
                            { label: "Weekly", value: rows.weekly, format: (n) => fmtUSD(n, { decimals: 0 }), testId: "calc-result-weekly" },
                            { label: "Bi-weekly", value: rows.biweekly, format: (n) => fmtUSD(n, { decimals: 0 }), testId: "calc-result-biweekly" },
                            { label: "Daily", value: rows.daily, format: (n) => fmtUSD(n, { decimals: 0 }), testId: "calc-result-daily" },
                        ]}
                    />
                </div>
            </div>

            {/* Browse take-home pay by state — internal-link to 50 paycheck pages */}
            <div className="mt-10 bg-card border border-border rounded-xl p-6 sm:p-8">
                <div className="text-[11px] uppercase tracking-[0.2em] text-accent">Take-home pay</div>
                <h3 className="font-serif text-2xl sm:text-3xl text-primary mt-1 mb-2">See your real paycheck — pick your state</h3>
                <p className="text-sm text-muted-foreground mb-5 max-w-2xl">
                    The numbers above are gross. For an after-tax breakdown including federal + state + FICA — broken down annual / monthly / biweekly / weekly / hourly — choose your state below.
                </p>
                <div className="flex flex-wrap gap-2" data-testid="salary-takehome-browse">
                    {STATE_TAX_LIST.map((s) => (
                        <Link
                            key={s.code}
                            to={`/calculator/take-home/${s.code}`}
                            data-testid={`salary-takehome-${s.code}`}
                            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                                s.noTax
                                    ? "border-accent/40 bg-accent/5 text-accent hover:border-accent"
                                    : "border-border text-primary hover:border-primary/40"
                            }`}
                        >
                            {s.name}
                            {s.noTax && <span className="ml-1 opacity-70">· no tax</span>}
                        </Link>
                    ))}
                </div>
            </div>
        </CalculatorShell>
    );
}
